import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Receipt, Loader2, Search, ChevronLeft, ChevronRight, X, Sparkles,
    CheckCircle2, XCircle, Clock, RotateCcw, Image as ImageIcon,
} from 'lucide-react';
import { Input } from './ui/input';
import { Dialog, DialogContent } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return iso; }
};

const STATUS_PILL = {
    pending:   { tone: 'bg-amber-500/15 border-amber-500/40 text-amber-300',   icon: Clock,        label: 'Pending'   },
    approved:  { tone: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300', icon: CheckCircle2, label: 'Approved'  },
    rejected:  { tone: 'bg-rose-500/15 border-rose-500/40 text-rose-300',       icon: XCircle,      label: 'Rejected'  },
    cancelled: { tone: 'bg-zinc-700/30 border-white/10 text-zinc-300',          icon: X,            label: 'Cancelled' },
};

const StatusPill = ({ row }) => {
    const cfg = STATUS_PILL[row.status] || STATUS_PILL.pending;
    const Icon = row.auto_credited ? Sparkles : cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${cfg.tone}`}>
            <Icon className="w-2.5 h-2.5" />
            {row.auto_credited ? 'Auto' : cfg.label}
            {row.clawed_back && <span className="ml-1 text-rose-300">· clawed</span>}
        </span>
    );
};

const STATUS_OPTS = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'auto_credited', label: 'Auto-credited' },
    { value: 'manual_approved', label: 'Manual approved' },
    { value: 'rejected', label: 'Rejected' },
];

/**
 * Searchable, paginated history of every payment receipt visible to the
 * caller. Drop into:
 *   – Admin → Reports as a global audit panel (no `userId` prop)
 *   – PlayerActivityDialog → "Receipts" tab (pass `userId`)
 *
 * Each row is clickable and opens a full-screen viewer with the original
 * screenshot, OCR details, and a clawback button for auto-credited rows.
 */
const ReceiptsHistoryPanel = ({ userId = null, compact = false } = {}) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('all');
    const [q, setQ] = useState('');
    const [qDebounced, setQDebounced] = useState('');
    const [txnId, setTxnId] = useState('');
    const [txnIdDebounced, setTxnIdDebounced] = useState('');
    const [loading, setLoading] = useState(false);
    const [viewingId, setViewingId] = useState(null);

    useEffect(() => {
        const id = setTimeout(() => setQDebounced(q.trim()), 300);
        return () => clearTimeout(id);
    }, [q]);

    useEffect(() => {
        const id = setTimeout(() => setTxnIdDebounced(txnId.trim()), 300);
        return () => clearTimeout(id);
    }, [txnId]);

    const fetchPage = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/staff/square-receipts/history`, {
                params: {
                    page, limit: 25,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    user_id: userId || undefined,
                    q: qDebounced || undefined,
                    txn_id: txnIdDebounced || undefined,
                },
                withCredentials: true,
            });
            setRows(data.rows || []);
            setTotal(data.total || 0);
            setPages(data.pages || 0);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load receipts');
        } finally { setLoading(false); }
    }, [page, statusFilter, userId, qDebounced, txnIdDebounced]);

    useEffect(() => { fetchPage(); }, [fetchPage]);
    useEffect(() => { setPage(1); }, [statusFilter, qDebounced, txnIdDebounced]);

    return (
        <div className={`card-dark ${compact ? 'p-3' : 'p-5'} space-y-3`} data-testid="receipts-history-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-[#FFD700]" />
                    <h3 className="text-white font-black text-sm">
                        {userId ? 'Receipt history' : 'All receipts'}
                        <span className="ml-2 text-[10px] text-[#A0AAB2] font-bold">{total} total</span>
                    </h3>
                </div>
                {loading && <Loader2 className="w-3.5 h-3.5 text-[#A0AAB2] animate-spin" />}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[#A0AAB2]" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by player name or phone…"
                        className="pl-8 h-8 bg-[#0a0a0a] border-white/10 text-white text-[12px]"
                        data-testid="receipts-history-search"
                    />
                </div>
                <div className="relative flex-1 min-w-[180px]">
                    <Receipt className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[#FFD700]" />
                    <Input
                        value={txnId}
                        onChange={(e) => setTxnId(e.target.value)}
                        placeholder="Search by PayPal txn ID…"
                        className="pl-8 h-8 bg-[#0a0a0a] border-[#FFD700]/30 text-white text-[12px] font-mono"
                        data-testid="receipts-history-txnid-search"
                        title="Find every screenshot ever submitted for a given PayPal / Venmo / CashApp / Square transaction ID — useful for refund disputes and duplicate-claim investigations."
                    />
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                    {STATUS_OPTS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => setStatusFilter(opt.value)}
                            className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border transition-colors ${
                                statusFilter === opt.value
                                    ? 'bg-[#FFD700]/20 border-[#FFD700]/60 text-[#FFD700]'
                                    : 'bg-zinc-800/60 border-white/10 text-zinc-400 hover:bg-zinc-700'
                            }`}
                            data-testid={`receipts-history-status-${opt.value}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Rows */}
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                {rows.length === 0 ? (
                    <div className="text-center py-8 text-[#A0AAB2] text-sm">
                        <ImageIcon className="w-6 h-6 mx-auto mb-2 opacity-40" />
                        No receipts match these filters
                    </div>
                ) : rows.map((r) => {
                    const amt = r.credit_amount ?? r.ocr_amount ?? r.claimed_amount ?? 0;
                    return (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() => setViewingId(r.id)}
                            className="w-full flex items-center gap-3 p-2 rounded-md bg-[#0a0a0a] border border-white/5 hover:bg-zinc-900 hover:border-[#FFD700]/40 transition-colors text-left"
                            data-testid={`receipts-history-row-${r.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-[12px] font-bold text-white truncate">{r.player_name || '(unknown player)'}</span>
                                    <StatusPill row={r} />
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-[#A0AAB2] mt-0.5">
                                    <span>{fmtDate(r.created_at)}</span>
                                    {r.approved_by_name && <span>· by {r.approved_by_name}</span>}
                                    {r.ocr_confidence && <span>· OCR: {r.ocr_confidence}</span>}
                                    {r.ocr_txn_id && (
                                        <span
                                            className="font-mono px-1.5 py-0.5 rounded bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] truncate max-w-[180px]"
                                            data-testid={`receipts-history-txnid-${r.id}`}
                                            title={`Transaction ID: ${r.ocr_txn_id}`}
                                        >
                                            #{r.ocr_txn_id}
                                        </span>
                                    )}
                                    {r.duplicate_txn_review_pending && (
                                        <span
                                            className="px-1.5 py-0.5 rounded bg-rose-500/20 border border-rose-500/50 text-rose-200 font-black uppercase tracking-wider"
                                            data-testid={`receipts-history-dup-${r.id}`}
                                        >
                                            Dup — Review
                                        </span>
                                    )}
                                    {r.reject_reason && <span className="text-rose-300 truncate">· {r.reject_reason}</span>}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-[14px] font-mono font-black text-emerald-300">{fmt(amt)}</div>
                                {r.ocr_merchant && <div className="text-[9px] text-[#A0AAB2]">{r.ocr_merchant}</div>}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Pagination */}
            {pages > 1 && (
                <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <span className="text-[10px] text-[#A0AAB2]">Page {page} of {pages}</span>
                    <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                            className="p-1 rounded border border-white/10 text-zinc-400 hover:bg-zinc-800 disabled:opacity-30">
                            <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                            className="p-1 rounded border border-white/10 text-zinc-400 hover:bg-zinc-800 disabled:opacity-30">
                            <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            )}

            <ReceiptViewerDialog
                receiptId={viewingId}
                onClose={() => setViewingId(null)}
            />
        </div>
    );
};

const ReceiptViewerDialog = ({ receiptId, onClose }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!receiptId) { setDetail(null); return; }
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/staff/square-receipts/${receiptId}/image`, { withCredentials: true });
                if (alive) setDetail(data);
            } catch (e) {
                if (alive) toast.error(e.response?.data?.detail || 'Failed to load receipt');
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [receiptId]);

    const open = !!receiptId;
    const img = detail?.image_b64 ? `data:image/png;base64,${detail.image_b64}` : null;
    const amt = detail ? (detail.credit_amount ?? detail.ocr_amount ?? detail.claimed_amount ?? 0) : 0;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="receipt-viewer-dialog">
                {loading || !detail ? (
                    <div className="py-12 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-[#A0AAB2] animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div>
                                <h3 className="text-white font-black text-base flex items-center gap-2">
                                    {detail.player_name || '(unknown player)'}
                                    <StatusPill row={detail} />
                                </h3>
                                <p className="text-[11px] text-[#A0AAB2]">{detail.player_phone} · {fmtDate(detail.created_at)}</p>
                            </div>
                            <div className="text-right">
                                <div className="text-[8px] uppercase tracking-widest text-emerald-400/80 font-bold">Amount</div>
                                <div className="text-2xl font-mono font-black text-emerald-300">{fmt(amt)}</div>
                            </div>
                        </div>

                        {/* OCR details */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                            <div className="bg-[#121212] rounded-md p-2 border border-white/5">
                                <p className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">OCR confidence</p>
                                <p className="text-white font-bold">{detail.ocr_confidence || '—'}</p>
                            </div>
                            <div className="bg-[#121212] rounded-md p-2 border border-white/5">
                                <p className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">OCR amount</p>
                                <p className="text-white font-bold">{fmt(detail.ocr_amount)}</p>
                            </div>
                            <div className="bg-[#121212] rounded-md p-2 border border-white/5">
                                <p className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">Merchant</p>
                                <p className="text-white font-bold truncate">{detail.ocr_merchant || '—'}</p>
                            </div>
                            <div className="bg-[#121212] rounded-md p-2 border border-white/5">
                                <p className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">Last 4</p>
                                <p className="text-white font-bold">{detail.ocr_last4 || '—'}</p>
                            </div>
                        </div>

                        {detail.approved_by_name && (
                            <div className="text-[11px] text-[#A0AAB2]">
                                {detail.auto_credited ? 'Auto-credited by system' : `Reviewed by ${detail.approved_by_name}`} on {fmtDate(detail.decided_at)}
                            </div>
                        )}
                        {detail.reject_reason && (
                            <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-2">
                                <strong>Reject reason:</strong> {detail.reject_reason}
                            </div>
                        )}
                        {detail.clawed_back && (
                            <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
                                <RotateCcw className="w-3 h-3 inline mr-1" />
                                <strong>Clawed back:</strong> {fmt(detail.clawback_amount)} reversed
                            </div>
                        )}

                        {/* Image */}
                        {img ? (
                            <div className="rounded-lg border border-white/10 overflow-hidden bg-black flex items-center justify-center max-h-[60vh]">
                                <img src={img} alt="Receipt" className="max-w-full max-h-[60vh] object-contain" />
                            </div>
                        ) : (
                            <div className="text-center py-8 text-[#A0AAB2] text-sm">
                                <ImageIcon className="w-6 h-6 mx-auto mb-2 opacity-40" />
                                Image not stored for this receipt.
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ReceiptsHistoryPanel;
